import React, { useEffect, useState, useCallback } from "react";

import EditIcon from "@mui/icons-material/Edit";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { type Theme } from "@mui/material/styles";

import {
  OrgRole,
  ROLE_METADATA,
  type OrgMember,
} from "src/domains/organization/types/OrganizationTypes";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import API from "src/platform/http/shared-http";
import debugLogger from "src/platform/utils/debugLogger";
import { ErrorMessage } from "src/shell/components/ErrorMessage";
import {
  getCardStyles,
  getEmptyStateSx,
  getFocusRingSx,
} from "src/shell/styles/themeUtils";

interface MembersTabProps {
  orgAlias: string;
}

const MembersTab: React.FC<MembersTabProps> = ({ orgAlias }) => {
  const theme = useTheme();
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<OrgRole>(OrgRole.DEVELOPER);
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const [editMember, setEditMember] = useState<OrgMember | null>(null);
  const [editRole, setEditRole] = useState<OrgRole>(OrgRole.DEVELOPER);
  const [savingRole, setSavingRole] = useState(false);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const api = new API(
        apiBaseUrls.BACKEND,
        `/api/v1/organizations/${orgAlias}/members`,
      );
      const data = await api.get<OrgMember[]>();
      setMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load members";
      setError("Failed to load members");
      debugLogger.error("members-tab", "Failed to load members", msg);
    } finally {
      setLoading(false);
    }
  }, [orgAlias]);

  useEffect(() => {
    void fetchMembers();
  }, [fetchMembers]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteError(null);
    try {
      const api = new API(
        apiBaseUrls.BACKEND,
        `/api/v1/organizations/${orgAlias}/members`,
      );
      const member = await api.post<OrgMember>({
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      setMembers([...members, member]);
      setInviteOpen(false);
      setInviteEmail("");
      setInviteRole(OrgRole.DEVELOPER);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to invite member";
      setInviteError(msg);
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async () => {
    if (!editMember) return;
    setSavingRole(true);
    try {
      const api = new API(
        apiBaseUrls.BACKEND,
        `/api/v1/organizations/${orgAlias}/members/${editMember.userId}`,
      );
      const updated = await api.patch<OrgMember>({ role: editRole });
      setMembers(
        members.map((m) => (m.userId === updated.userId ? updated : m)),
      );
      setEditMember(null);
    } catch (err) {
      debugLogger.error("members-tab", "Failed to update role", err);
    } finally {
      setSavingRole(false);
    }
  };

  const getRoleChip = (role: string) => {
    const metadata =
      ROLE_METADATA[role as OrgRole] ?? ROLE_METADATA[OrgRole.VIEWER];
    return (
      <Chip
        label={metadata.label}
        size="small"
        color={metadata.color}
        variant={role === OrgRole.ADMIN ? "filled" : "outlined"}
        sx={{ minWidth: 80, fontWeight: 500 }}
      />
    );
  };

  const formatDate = (value?: string) => {
    if (!value) return "—";
    return new Date(value).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <Card sx={getCardStyles(theme)}>
        <Stack spacing={0}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                borderBottom:
                  i < 2
                    ? `1px solid ${alpha(theme.palette.divider, 0.06)}`
                    : "none",
              }}
            >
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width={140} height={20} />
                <Skeleton width={200} height={16} />
              </Box>
              <Skeleton width={80} height={24} />
            </Box>
          ))}
        </Stack>
      </Card>
    );
  }

  if (error) {
    return <ErrorMessage message={error} sx={{ my: 2 }} />;
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
          {members.length} member{members.length !== 1 ? "s" : ""}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PersonAddIcon />}
          onClick={() => setInviteOpen(true)}
          sx={{ textTransform: "none", fontWeight: 500 }}
        >
          Invite Member
        </Button>
      </Stack>

      {members.length === 0 ? (
        <Box sx={getEmptyStateSx(theme)}>
          <GroupIcon sx={{ fontSize: 40, color: "text.disabled" }} />
          <Typography
            variant="subtitle2"
            fontWeight={500}
            color="text.secondary"
          >
            No members yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Invite team members to collaborate on this organization.
          </Typography>
        </Box>
      ) : (
        <Card sx={getCardStyles(theme)}>
          <TableContainer>
            <Table sx={{ minWidth: 520 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: "40%" }}>
                    User
                  </TableCell>
                  <TableCell sx={headerCellSx}>Role</TableCell>
                  <TableCell sx={headerCellSx}>Joined</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member, index) => (
                  <TableRow
                    key={member.id}
                    sx={{
                      transition: "background 150ms ease",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      },
                      "& td": {
                        borderBottom:
                          index < members.length - 1
                            ? `1px solid ${alpha(theme.palette.divider, 0.06)}`
                            : "none",
                        py: 1.5,
                      },
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          sx={{
                            bgcolor: "primary.main",
                            width: 36,
                            height: 36,
                            fontSize: "0.875rem",
                          }}
                        >
                          {(
                            member.user.firstName?.[0] ??
                            member.user.email[0] ??
                            "?"
                          ).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {member.user.firstName && member.user.lastName
                              ? `${member.user.firstName} ${member.user.lastName}`
                              : member.user.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {member.user.email}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>{getRoleChip(member.role)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(member.joinedAt ?? member.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          member.status === "Invited" ? "Invited" : "Active"
                        }
                        size="small"
                        color={
                          member.status === "Invited" ? "warning" : "success"
                        }
                        variant="outlined"
                        sx={{ minWidth: 70, fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditMember(member);
                          setEditRole(member.role as OrgRole);
                        }}
                        sx={{
                          color: "text.disabled",
                          "&:hover": { color: "primary.main" },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog
        open={inviteOpen}
        onClose={() => !inviting && setInviteOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Invite Member</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {inviteError && (
              <Alert severity="error" sx={{ borderRadius: 1 }}>
                {inviteError}
              </Alert>
            )}
            <TextField
              label="Email"
              fullWidth
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="user@example.com"
              disabled={inviting}
              sx={getFocusRingSx(theme)}
            />
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: "text.secondary" }}
              >
                Role
              </Typography>
              <Select
                fullWidth
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as OrgRole)}
                disabled={inviting}
                sx={getFocusRingSx(theme)}
              >
                {Object.values(OrgRole).map((role: OrgRole) => (
                  <MenuItem key={role} value={role}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {ROLE_METADATA[role].label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ROLE_METADATA[role].description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setInviteOpen(false)}
            disabled={inviting}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleInvite()}
            disabled={inviting || !inviteEmail.trim()}
            sx={{
              textTransform: "none",
              "&:active": { transform: "scale(0.98)" },
            }}
          >
            {inviting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Invite"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!editMember}
        onClose={() => !savingRole && setEditMember(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Manage Role</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Update role for{" "}
              <strong>
                {editMember?.user.firstName && editMember?.user.lastName
                  ? `${editMember.user.firstName} ${editMember.user.lastName}`
                  : editMember?.user.email}
              </strong>
            </Typography>
            <Select
              fullWidth
              value={editRole}
              onChange={(e) => setEditRole(e.target.value as OrgRole)}
              disabled={savingRole}
              sx={getFocusRingSx(theme)}
            >
              {Object.values(OrgRole).map((role: OrgRole) => (
                <MenuItem key={role} value={role}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {ROLE_METADATA[role].label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {ROLE_METADATA[role].description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setEditMember(null)}
            disabled={savingRole}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleRoleChange()}
            disabled={savingRole}
            sx={{
              textTransform: "none",
              "&:active": { transform: "scale(0.98)" },
            }}
          >
            {savingRole ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Save"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

const headerCellSx = {
  fontSize: "0.6875rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "text.secondary",
  borderBottom: (theme: Theme) =>
    `1px solid ${alpha(theme.palette.divider, 0.06)}`,
};

export default MembersTab;
